import React, { useEffect, useState } from "react";
import { useLang } from "../contexts/LanguageContext";
import { api } from "../lib/api";
import ArticleCard from "./ArticleCard";
import SectionHeader from "./SectionHeader";

const FILTER_BY_CATEGORY = {
  war_crime: ["country", "region", "violation_type"],
  terror_crime: ["country", "organization"],
  monitoring: ["country", "organization"],
  study: ["country"],
  analysis: ["country"],
  news: ["country"],
  director_message: [],
  publication: [],
};

export default function ArticleListPage({
  category,
  eyebrow,
  title,
  subtitle,
  heroImage,
  testId,
}) {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [facets, setFacets] = useState({ countries: [], organizations: [], regions: [], violation_types: [] });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({});
  const [q, setQ] = useState("");

  const filterFields = FILTER_BY_CATEGORY[category] || [];

  const load = async () => {
    setLoading(true);
    try {
      const params = { category, limit: 60 };
      Object.entries(filters).forEach(([k, v]) => {
        if (v) params[k] = v;
      });
      if (q) params.q = q;
      const { data } = await api.get("/articles", { params });
      setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/articles/facets").then(({ data }) => setFacets(data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, JSON.stringify(filters), q]);

  return (
    <div data-testid={testId}>
      <SectionHeader eyebrow={eyebrow} title={title} subtitle={subtitle} image={heroImage} />

      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-12">
        {/* Filter bar */}
        <div className="flex flex-wrap items-center gap-3 mb-10 pb-6 border-b border-slate200">
          <input
            data-testid="list-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t.filters.search_placeholder}
            className="flex-1 min-w-[220px] bg-transparent border border-slate200 px-3 h-10 text-[13px] outline-none focus:border-ink"
          />
          {filterFields.includes("country") && (
            <select
              data-testid="filter-country"
              value={filters.country || ""}
              onChange={(e) => setFilters((f) => ({ ...f, country: e.target.value }))}
              className="bg-transparent border border-slate200 px-3 h-10 text-[13px] outline-none focus:border-ink"
            >
              <option value="">{t.filters.country}: {t.filters.all}</option>
              {facets.countries.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          )}
          {filterFields.includes("organization") && (
            <select
              data-testid="filter-organization"
              value={filters.organization || ""}
              onChange={(e) => setFilters((f) => ({ ...f, organization: e.target.value }))}
              className="bg-transparent border border-slate200 px-3 h-10 text-[13px] outline-none focus:border-ink"
            >
              <option value="">{t.filters.organization}: {t.filters.all}</option>
              {facets.organizations.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          )}
          {filterFields.includes("region") && (
            <select
              data-testid="filter-region"
              value={filters.region || ""}
              onChange={(e) => setFilters((f) => ({ ...f, region: e.target.value }))}
              className="bg-transparent border border-slate200 px-3 h-10 text-[13px] outline-none focus:border-ink"
            >
              <option value="">{t.filters.region}: {t.filters.all}</option>
              {facets.regions.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          )}
          {filterFields.includes("violation_type") && (
            <select
              data-testid="filter-violation-type"
              value={filters.violation_type || ""}
              onChange={(e) => setFilters((f) => ({ ...f, violation_type: e.target.value }))}
              className="bg-transparent border border-slate200 px-3 h-10 text-[13px] outline-none focus:border-ink"
            >
              <option value="">{t.filters.violation_type}: {t.filters.all}</option>
              {facets.violation_types.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          )}
          {(Object.values(filters).some(Boolean) || q) && (
            <button
              data-testid="filter-reset"
              onClick={() => { setFilters({}); setQ(""); }}
              className="px-3 h-10 text-[12px] uppercase tracking-wider border border-slate200 hover:border-ink"
            >
              {t.filters.reset}
            </button>
          )}
        </div>

        {loading ? (
          <div className="text-center text-slate500 py-20">{t.common.loading}</div>
        ) : items.length === 0 ? (
          <div className="text-center text-slate500 py-20" data-testid="list-empty">
            {t.common.no_results}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-7">
            {items.map((a) => (
              <ArticleCard key={a.id} article={a} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
